import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useErrorReport } from '../../contexts/ErrorReportContext';
import { dismissInstallOffer } from './dismissInstallOffer';
import { INSTALL_BENEFIT_COPY, shouldOfferInstall, useInstallApp } from './useInstallApp';
import './InstallAppOffer.css';

/** Install action. `installed` overrides the live display mode in tests. */
export default function InstallAppOffer({ variant = 'bar', installed, raised = false }) {
  const { currentUser, userProfile, profileReady } = useAuth();
  const { reportError } = useErrorReport();
  const live = useInstallApp();
  const [optimisticDismissed, setOptimisticDismissed] = useState(false);
  const pendingRef = useRef(false);

  const signedIn = Boolean(currentUser) && !currentUser.isAnonymous && profileReady;
  const dismissed = userProfile?.installOfferDismissed === true || optimisticDismissed;
  const uid = currentUser?.uid || '';
  const hidden = !shouldOfferInstall({
    standalone: installed ?? live.installed,
    signedIn,
    dismissed,
  });

  useEffect(() => {
    if (variant !== 'bar' || typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    if (hidden) {
      delete root.dataset.installOffer;
      root.style.removeProperty('--install-offer-height');
      return undefined;
    }
    root.dataset.installOffer = '1';
    root.style.setProperty('--install-offer-height', '4.05rem');
    return () => {
      delete root.dataset.installOffer;
      root.style.removeProperty('--install-offer-height');
    };
  }, [variant, hidden]);

  const persistDismiss = async () => {
    if (pendingRef.current || !uid) return;
    pendingRef.current = true;
    setOptimisticDismissed(true);
    try {
      await dismissInstallOffer(uid);
    } catch (err) {
      pendingRef.current = false;
      setOptimisticDismissed(false);
      reportError(err, {
        pageLabel: 'Header',
        source: 'install-offer-dismiss',
        userMessage: 'Could not hide the install bar.',
      });
    }
  };

  const onInstall = async () => {
    await live.install();
    await persistDismiss();
  };

  if (hidden) return null;

  const className = [
    'install-app-offer',
    variant === 'panel' ? 'install-app-offer--panel' : 'install-app-offer--bar',
    raised ? 'install-app-offer--raised' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <p className="install-app-offer__copy">{INSTALL_BENEFIT_COPY}</p>
      <button type="button" className="install-app-offer__btn" onClick={onInstall}>
        Install
      </button>
      {variant === 'bar' ? (
        <button
          type="button"
          className="install-app-offer__dismiss"
          aria-label="Dismiss install"
          onClick={persistDismiss}
        >
          ×
        </button>
      ) : null}
      {live.hint ? <p className="install-app-offer__hint">{live.hint}</p> : null}
    </div>
  );
}
